import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ScrollText, CheckCircle2, Loader2, Info } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { friendlyError } from "@/lib/errors";

const US_STATE_CODES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "DC", "FL", "GA", "HI",
  "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN",
  "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH",
  "OK", "OR", "PA", "PR", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA",
  "WA", "WV", "WI", "WY",
];

const BLANK = {
  type: "attorney",
  full_name: "",
  firm_name: "",
  email: "",
  phone: "",
  text_number: "",
  address: "",
  city: "",
  state: "",
  zip: "",
  languages: "",       // comma-separated in UI
  website: "",
  bio: "",
  bar_state: "",
  bar_number: "",
  bar_admission_date: "",
  services_offered: "", // comma-separated in UI
  years_experience: "",
};

export default function DirectoryApplyPage() {
  const [form, setForm] = useState(BLANK);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const nav = useNavigate();

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const isAttorney = form.type === "attorney";

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const payload = {
      type: form.type,
      full_name: form.full_name.trim(),
      firm_name: form.firm_name.trim() || null,
      email: form.email.trim(),
      phone: form.phone.trim(),
      text_number: form.text_number.trim() || null,
      address: form.address.trim(),
      city: form.city.trim(),
      state: form.state.trim().toUpperCase(),
      zip: form.zip.trim(),
      languages: form.languages.split(",").map((s) => s.trim()).filter(Boolean),
      website: form.website.trim() || null,
      bio: form.bio.trim() || null,
    };
    if (isAttorney) {
      payload.bar_state = form.bar_state.trim().toUpperCase();
      payload.bar_number = form.bar_number.trim();
      payload.bar_admission_date = form.bar_admission_date.trim();
    } else {
      payload.services_offered = form.services_offered
        .split(",").map((s) => s.trim()).filter(Boolean);
      payload.years_experience = form.years_experience === ""
        ? null : parseInt(form.years_experience, 10);
    }
    try {
      await api.post("/directory/apply", payload);
      setDone(true);
      toast.success("Application received — we'll review it shortly.");
    } catch (err) {
      toast.error(friendlyError(err, "We couldn't submit your application. Check your entries and try again."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-app)" }}>
      <TopBar />
      <div className="mx-auto max-w-3xl px-6 py-10" data-testid="directory-apply-page">
        {done ? <SuccessBlock nav={nav} /> : (
          <>
            <div className="mb-6">
              <div className="pill pill-ai mb-3 inline-flex">Directory application</div>
              <h1 className="font-display text-3xl font-extrabold mb-2" style={{ color: "var(--text-primary)" }}>
                Request to be listed
              </h1>
              <p className="text-sm max-w-2xl" style={{ color: "var(--text-secondary)" }}>
                Listings are free. DS-260USA.com does not vet applicants and does not
                endorse anyone in the directory. We review applications for basic completeness
                only, then publish them as-is. It is the assistant&apos;s responsibility to keep
                their information current and truthful.
              </p>
            </div>

            <form onSubmit={submit} className="card-surface p-6 space-y-5">
              <TypeToggle value={form.type} onChange={set("type")} />

              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Full name (as licensed / professionally used)" required value={form.full_name} onChange={set("full_name")} testId="apply-full-name" />
                <Field label="Firm / business name" value={form.firm_name} onChange={set("firm_name")} testId="apply-firm" />
                <Field label="Email (public)" required type="email" value={form.email} onChange={set("email")} testId="apply-email" />
                <Field label="Phone (public)" required value={form.phone} onChange={set("phone")} testId="apply-phone" />
                <Field label="Text-message number (optional)" value={form.text_number} onChange={set("text_number")} testId="apply-text" />
                <Field label="Website (optional)" value={form.website} onChange={set("website")} testId="apply-website" placeholder="https://…" />
              </div>

              <div>
                <label className="field-label">Office address <span style={{ color: "#B91C1C" }}>*</span></label>
                <input className="field-input" required value={form.address} onChange={(e) => set("address")(e.target.value)} data-testid="apply-address" placeholder="Street address" />
                <div className="grid grid-cols-3 gap-3 mt-2">
                  <input className="field-input" required placeholder="City" value={form.city} onChange={(e) => set("city")(e.target.value)} data-testid="apply-city" />
                  <select className="field-input" required value={form.state} onChange={(e) => set("state")(e.target.value)} data-testid="apply-state">
                    <option value="">State…</option>
                    {US_STATE_CODES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <input className="field-input" required placeholder="ZIP" value={form.zip} onChange={(e) => set("zip")(e.target.value)} data-testid="apply-zip" />
                </div>
              </div>

              <Field label="Languages spoken (comma-separated)" value={form.languages} onChange={set("languages")} testId="apply-languages"
                     placeholder="English, Spanish, Mandarin" />

              {isAttorney ? (
                <div className="rounded-md p-4 space-y-3"
                     style={{ background: "var(--ai-bg)", border: "1px solid var(--ai-border)" }}>
                  <div className="font-display font-bold text-sm" style={{ color: "var(--ai-text)" }}>
                    Bar credentials (required for attorneys)
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="field-label">Bar state <span style={{ color: "#B91C1C" }}>*</span></label>
                      <select className="field-input" required value={form.bar_state} onChange={(e) => set("bar_state")(e.target.value)} data-testid="apply-bar-state">
                        <option value="">Pick…</option>
                        {US_STATE_CODES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <Field label="Bar #" required value={form.bar_number} onChange={set("bar_number")} testId="apply-bar-number" />
                    <Field label="Admitted (YYYY-MM-DD)" required value={form.bar_admission_date} onChange={set("bar_admission_date")} testId="apply-bar-date" placeholder="2015-06-12" />
                  </div>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Services offered (comma-separated)" value={form.services_offered} onChange={set("services_offered")} testId="apply-services"
                         placeholder="DS-260 form prep, civil-document organization, translation coordination" />
                  <Field label="Years of experience" value={form.years_experience} onChange={set("years_experience")} testId="apply-years" placeholder="e.g. 8" />
                </div>
              )}

              <div>
                <label className="field-label">Short bio / description (optional, max 800 chars)</label>
                <textarea className="field-input font-mono text-xs" rows={4} value={form.bio}
                          onChange={(e) => set("bio")(e.target.value)} data-testid="apply-bio" />
              </div>

              <div className="text-xs p-3 rounded-md" style={{ background: "var(--warn-bg)", color: "var(--warn-text)", border: "1px solid var(--warn-border)" }}>
                <Info className="w-3.5 h-3.5 inline mr-1" />
                By submitting this application you certify that (a) all information is truthful,
                (b) you are authorized to be listed as an assistant, and (c) you understand that
                DS-260USA.com is not endorsing you and receives no fee for your listing.
              </div>

              <div className="flex items-center gap-3">
                <button type="submit" disabled={submitting} className="btn-primary" data-testid="apply-submit">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : null}
                  {submitting ? "Submitting…" : "Submit application"}
                </button>
                <Link to="/directory" className="btn-ghost text-sm">Cancel</Link>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function TopBar() {
  return (
    <header className="sticky top-0 z-30 backdrop-blur-md border-b"
            style={{ borderColor: "var(--border-stone)", background: "rgba(249,248,245,0.90)" }}>
      <div className="mx-auto max-w-7xl px-6 py-3 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg flex items-center justify-center"
               style={{ background: "var(--action)" }}>
            <ScrollText className="w-4 h-4 text-white" />
          </div>
          <div className="leading-tight">
            <div className="font-display font-bold text-[16px]" style={{ color: "var(--text-primary)" }}>
              DS-260USA<span style={{ color: "var(--action)" }}>.com</span>
            </div>
            <div className="text-[10.5px] uppercase tracking-wider" style={{ color: "var(--text-secondary)" }}>
              Assistant Application
            </div>
          </div>
        </Link>
        <Link to="/directory" className="btn-ghost text-sm flex items-center gap-1">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to directory
        </Link>
      </div>
    </header>
  );
}

function TypeToggle({ value, onChange }) {
  const opt = (v, label, sub) => (
    <button
      type="button"
      onClick={() => onChange(v)}
      className={`flex-1 p-4 rounded-lg text-left transition-colors ${value === v ? "ring-2" : ""}`}
      style={{
        background: value === v ? "var(--ai-bg)" : "#FFFFFF",
        border: `1px solid ${value === v ? "var(--ai-border)" : "var(--border-stone)"}`,
        color: "var(--text-primary)",
      }}
      data-testid={`apply-type-${v}`}
    >
      <div className="font-display font-bold text-sm">{label}</div>
      <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>{sub}</div>
    </button>
  );
  return (
    <div>
      <label className="field-label mb-2">I am applying as:</label>
      <div className="flex gap-3 flex-wrap">
        {opt("attorney", "Licensed Immigration Attorney", "You must provide your state bar and bar #.")}
        {opt("paralegal", "Paralegal / Form-Filler Assistant", "Non-attorneys who help petitioners with USCIS forms.")}
      </div>
    </div>
  );
}

function Field({ label, required, value, onChange, testId, placeholder, type = "text" }) {
  return (
    <div>
      <label className="field-label">
        {label}{required && <span style={{ color: "#B91C1C" }}> *</span>}
      </label>
      <input
        type={type}
        className="field-input"
        value={value}
        placeholder={placeholder}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
      />
    </div>
  );
}

function SuccessBlock({ nav }) {
  return (
    <div className="card-surface p-10 text-center" data-testid="apply-success">
      <CheckCircle2 className="w-14 h-14 mx-auto mb-4" style={{ color: "#065F46" }} />
      <h1 className="font-display text-2xl font-bold mb-2">Application received</h1>
      <p className="text-sm max-w-md mx-auto mb-6" style={{ color: "var(--text-secondary)" }}>
        Thank you. We&apos;ll review your submission for basic completeness and add you to the directory once approved.
        We&apos;ll reach out at the email address you provided if we need anything else.
      </p>
      <div className="flex items-center justify-center gap-3">
        <Link to="/directory" className="btn-primary text-sm">Back to directory</Link>
        <button type="button" onClick={() => nav(0)} className="btn-ghost text-sm">Submit another</button>
      </div>
    </div>
  );
}
